import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Download, Loader2, FileText, CheckCircle2, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';

const STATUS = {
  eingereicht: { label: 'Eingereicht', color: GOLD_INK },
  angenommen: { label: 'Angenommen', color: '#2F7A3D' },
  ueberarbeiten: { label: 'Überarbeiten', color: TERRA },
};

export default function AdminFacharbeitenPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [subs, setSubs] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [feedbackDraft, setFeedbackDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/facharbeiten`, getAuthHeader());
      setSubs(r.data.submissions || []);
    } catch { setSubs([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (s) => {
    try {
      const r = await axios.get(`${API}/admin/facharbeit/${s.id}/download`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${s.user_name}_${s.filename}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const review = async (s, status) => {
    setBusyId(s.id);
    try {
      await axios.post(`${API}/admin/facharbeit/${s.id}/review`,
        { status, feedback: feedbackDraft[s.id] ?? s.feedback ?? '' }, getAuthHeader());
      toast.success('Bewertung gespeichert');
      await load();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    setBusyId(null);
  };

  const content = (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <p className="font-mono text-[10px] uppercase font-semibold mb-2" style={{ letterSpacing: '0.24em', color: GOLD_INK }}>
        Admin · Prüfung
      </p>
      <h1 className="font-display text-3xl sm:text-4xl text-espresso mb-6">Facharbeiten</h1>

      {subs === null && (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-terra" /></div>
      )}

      {subs && subs.length === 0 && (
        <p className="font-display italic text-[17px] text-sand-600 py-10" data-testid="facharbeiten-empty">
          Noch keine Facharbeiten eingereicht.
        </p>
      )}

      <div className="space-y-4" data-testid="facharbeiten-list">
        {(subs || []).map((s) => {
          const st = STATUS[s.status] || STATUS.eingereicht;
          return (
            <div key={s.id} className="bg-cream-100 border border-cream-300 rounded-2xl p-4 sm:p-5" data-testid={`facharbeit-row-${s.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <FileText className="w-5 h-5 text-sand-500 flex-shrink-0 hidden sm:block mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className="text-[15px] font-semibold text-espresso">{s.user_name}</p>
                    <span className="font-mono text-[9px] uppercase font-bold px-2 py-0.5 rounded-full border" style={{ color: st.color, borderColor: st.color, letterSpacing: '0.14em' }}>
                      {st.label}
                    </span>
                  </div>
                  <p className="text-[12.5px] text-sand-600 mt-0.5">{s.course_title}</p>
                  <p className="text-[12px] text-sand-500 mt-0.5 truncate">
                    {s.filename} · {(s.size / 1024 / 1024).toFixed(1)} MB · V{s.version} · {new Date(s.uploaded_at).toLocaleString('de-DE')}
                  </p>
                  <textarea
                    defaultValue={s.feedback || ''}
                    onChange={(e) => setFeedbackDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                    placeholder="Feedback für den Teilnehmer (optional)…"
                    rows={2}
                    className="mt-3 w-full px-3 py-2 bg-cream-50 border border-cream-300 rounded-xl text-[13px] text-sand-800 focus:outline-none focus:border-terra resize-y"
                    data-testid={`facharbeit-feedback-input-${s.id}`}
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => download(s)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase border border-cream-400 text-sand-700 hover:bg-cream-200 transition-colors" style={{ letterSpacing: '0.12em' }} data-testid={`facharbeit-download-${s.id}`}>
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    {busyId === s.id ? <Loader2 className="w-5 h-5 animate-spin text-terra mt-1.5" /> : (
                      <>
                        <button onClick={() => review(s, 'angenommen')} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase transition-colors" style={{ backgroundColor: '#2F7A3D', color: '#FBF7F0', letterSpacing: '0.12em' }} data-testid={`facharbeit-accept-${s.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Annehmen
                        </button>
                        <button onClick={() => review(s, 'ueberarbeiten')} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase border transition-colors" style={{ borderColor: TERRA, color: TERRA, letterSpacing: '0.12em' }} data-testid={`facharbeit-revise-${s.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" /> Überarbeiten
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
}
